const MODULE = document.querySelectorAll('[data-module="post_article"]');

const LINK_BASE = [
    'flex',
    'items-center',
    'border-l-[3px]',
    'border-transparent',
    'pl-2',
    'py-2.5',
    'font-sans',
    'font-semibold',
    'text-base',
    'text-cinza-escuro',
    'transition-colors',
    'duration-300',
    'hover:text-principal',
    'focus-visible:outline',
    'focus-visible:outline-2',
    'focus-visible:outline-principal',
];
const LINK_ACTIVE = [
    'border-principal',
    'text-principal',
];
class PostArticle {
    constructor(component) {
        this.component = component;
        this.body = component.querySelector('[data-post-content-body]');
        this.lists = Array.from(component.querySelectorAll('[data-post-content-index-list]'))
            .filter(list => list.closest('[data-module]') === component);
        this.mobile = component.querySelector('[data-post-article-index-mobile]');
        this.desktop = component.querySelector('[data-post-article-index-desktop]');
        this.toggle = this.mobile?.querySelector('[data-post-content-index-toggle]');
        this.chevron = this.mobile?.querySelector('[data-post-content-index-chevron]');
        this.headings = [];
        this.links = [];
        this.sidebar = component.querySelector('[data-post-article-sidebar]');
        this.progress = component.querySelector('[data-post-reading-progress]');

        this.enhanceTables();
        this.init();
    }

    enhanceTables() {
        if (!this.body) {
            return;
        }

        const wrappers = [];

        this.body.querySelectorAll('[data-module="post_text"] table').forEach(table => {
            if (
                table.parentElement?.closest('table') ||
                table.closest('[data-post-table-scroll]')
            ) {
                return;
            }

            this.enhanceTableHeaders(table);

            const wrapper = document.createElement('div');
            const caption = table.caption?.textContent.trim();

            wrapper.dataset.postTableScroll = '';
            wrapper.setAttribute('role', 'region');
            wrapper.setAttribute('aria-label', caption || 'Tabela do artigo');
            table.before(wrapper);
            wrapper.appendChild(table);
            wrappers.push(wrapper);
        });

        const updateOverflow = () => {
            wrappers.forEach(wrapper => {
                if (wrapper.scrollWidth > wrapper.clientWidth + 1) {
                    wrapper.tabIndex = 0;
                } else {
                    wrapper.removeAttribute('tabindex');
                }
            });
        };

        updateOverflow();

        if (wrappers.length && typeof ResizeObserver !== 'undefined') {
            this.tableResizeObserver = new ResizeObserver(updateOverflow);
            wrappers.forEach(wrapper => {
                this.tableResizeObserver.observe(wrapper);
                this.tableResizeObserver.observe(wrapper.firstElementChild);
            });
        } else if (wrappers.length) {
            window.addEventListener('resize', updateOverflow, { passive: true });
        }
    }

    enhanceTableHeaders(table) {
        const firstBody = table.tBodies[0];
        const firstRow = firstBody?.rows[0];

        if (
            !table.tHead &&
            firstRow?.cells.length > 1 &&
            Array.from(firstRow.cells).every(cell => this.isPlainBoldTableCell(cell))
        ) {
            const head = document.createElement('thead');

            Array.from(firstRow.cells).forEach(cell => this.promoteTableCell(cell, 'col'));
            head.appendChild(firstRow);
            table.insertBefore(head, firstBody);
        }

        if (table.tHead) {
            Array.from(table.tBodies).forEach(body => {
                Array.from(body.rows).forEach(row => {
                    const firstCell = row.cells[0];

                    if (row.cells.length > 1 && this.isPlainBoldTableCell(firstCell)) {
                        this.promoteTableCell(firstCell, 'row');
                    }
                });
            });
        }
    }

    isPlainBoldTableCell(cell) {
        if (cell?.tagName !== 'TD') {
            return false;
        }

        const nodes = Array.from(cell.childNodes).filter(node => (
            node.nodeType !== Node.TEXT_NODE || node.textContent.trim()
        ));
        const bold = nodes[0];

        return nodes.length === 1 &&
            ['B', 'STRONG'].includes(bold?.nodeName) &&
            bold.textContent.trim().length > 0 &&
            Array.from(bold.childNodes).every(node => node.nodeType === Node.TEXT_NODE);
    }

    promoteTableCell(cell, scope) {
        const heading = document.createElement('th');

        Array.from(cell.attributes).forEach(attribute => {
            heading.setAttribute(attribute.name, attribute.value);
        });
        heading.setAttribute('scope', scope);

        while (cell.firstChild) {
            heading.appendChild(cell.firstChild);
        }

        cell.replaceWith(heading);
    }

    init() {
        if (
            !this.body ||
            !this.lists.length ||
            this.component.dataset.postContentInitialized === 'true'
        ) {
            return;
        }

        this.component.dataset.postContentInitialized = 'true';
        this.bindReadingProgress();
        this.collectHeadings();

        if (!this.headings.length) {
            this.hideIndex();
            return;
        }

        this.buildIndex();
        this.bindMobileToggle();
        this.bindScrollSpy();
    }

    collectHeadings() {
        const headings = this.body.querySelectorAll('h2, h3');

        headings.forEach((heading, index) => {
            if (
                heading.closest('[data-internal-hero], [data-post-related], [data-post-author], [data-post-content-sidebar], [data-article-slide]') ||
                heading.querySelector('[data-article-link], [data-accordeon-trigger]')
            ) {
                return;
            }

            const text = heading.textContent.trim();
            if (!text) {
                return;
            }

            const baseId = heading.id || this.slugify(text) || `post-content-section-${index}`;
            let id = baseId;
            let suffix = 2;

            const occupiedIds = new Set(
                Array.from(document.querySelectorAll('[id]'))
                    .filter(element => element !== heading)
                    .map(element => element.id)
            );

            while (occupiedIds.has(id)) {
                id = `${baseId}-${suffix}`;
                suffix += 1;
            }

            heading.id = id;
            heading.tabIndex = -1;

            heading.style.scrollMarginTop = `${this.scrollOffset()}px`;
            this.headings.push(heading);
        });
    }

    buildIndex() {
        this.lists.forEach(list => {
            this.headings.forEach(heading => {
                const item = document.createElement('li');
                const link = document.createElement('a');

                link.href = `#${encodeURIComponent(heading.id)}`;
                link.textContent = heading.textContent.trim();
                link.dataset.target = heading.id;
                link.classList.add(...LINK_BASE);

                if (heading.tagName === 'H3') {
                    link.classList.add('ml-3', 'text-sm');
                }

                link.addEventListener('click', event => {
                    event.preventDefault();
                    this.closeMobile();
                    this.scrollTo(heading);
                });

                item.appendChild(link);
                list.appendChild(item);
                this.links.push(link);
            });
        });
    }

    scrollTo(heading) {
        const top = heading.getBoundingClientRect().top + window.pageYOffset - this.scrollOffset();
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        heading.focus({ preventScroll: true });
        window.scrollTo({
            top,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
    }

    setActive(id) {
        if (this.activeId === id) {
            return;
        }

        this.activeId = id;

        this.links.forEach(link => {
            const isActive = link.dataset.target === id;
            link.classList.toggle(LINK_ACTIVE[0], isActive);
            link.classList.toggle(LINK_ACTIVE[1], isActive);
            link.classList.toggle('border-transparent', !isActive);
            link.classList.toggle('text-cinza-escuro', !isActive);

            if (isActive) {
                link.setAttribute('aria-current', 'location');
            } else {
                link.removeAttribute('aria-current');
            }
        });

        this.revealActiveLink(id);
    }

    revealActiveLink(id) {
        if (
            !this.sidebar ||
            !window.matchMedia('(min-width: 1024px)').matches
        ) {
            return;
        }

        const link = this.links.find(link => (
            link.dataset.target === id && this.sidebar.contains(link)
        ));

        if (!link) {
            return;
        }

        const sidebar = this.sidebar.getBoundingClientRect();
        const rectangle = link.getBoundingClientRect();
        const padding = 16;

        if (
            rectangle.top >= sidebar.top + padding &&
            rectangle.bottom <= sidebar.bottom - padding
        ) {
            return;
        }

        const top = this.sidebar.scrollTop
            + rectangle.top
            - sidebar.top
            - (this.sidebar.clientHeight - rectangle.height) / 2;

        this.sidebar.scrollTo({
            top,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
        });
    }

    bindScrollSpy() {
        const onScroll = () => {
            let current = this.headings[0];

            this.headings.forEach(heading => {
                if (heading.getBoundingClientRect().top - this.scrollOffset() <= 1) {
                    current = heading;
                }
            });

            if (current) {
                this.setActive(current.id);
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', () => {
            if (this.activeId) {
                this.revealActiveLink(this.activeId);
            }
        });
        onScroll();
    }

    bindMobileToggle() {
        if (!this.toggle || !this.mobile) {
            return;
        }

        const list = this.mobile.querySelector('[data-post-content-index-list]');
        if (!list) {
            return;
        }

        this.toggle.addEventListener('click', () => {
            const isOpen = !list.classList.contains('hidden');

            list.classList.toggle('hidden', isOpen);
            list.classList.toggle('flex', !isOpen);
            this.toggle.setAttribute('aria-expanded', String(!isOpen));

            if (this.chevron) {
                this.chevron.classList.toggle('rotate-180', !isOpen);
            }
        });
    }

    closeMobile() {
        if (!this.toggle || !this.mobile) {
            return;
        }

        const list = this.mobile.querySelector('[data-post-content-index-list]');
        if (!list || list.classList.contains('hidden')) {
            return;
        }

        list.classList.add('hidden');
        list.classList.remove('flex');
        this.toggle.setAttribute('aria-expanded', 'false');
        if (this.chevron) {
            this.chevron.classList.remove('rotate-180');
        }
    }

    hideIndex() {
        this.mobile?.remove();
        this.desktop?.remove();

        const sidebar = this.sidebar;

        if (sidebar && !sidebar.children.length) {
            sidebar.remove();
        }
    }

    scrollOffset() {
        const header = document.querySelector('[data-module="header"]');
        const bar = header?.querySelector('[data-header-bar]');

        return Math.max(0, bar?.getBoundingClientRect().bottom || 70) + 24;
    }

    bindReadingProgress() {
        const update = () => {
            const offset = this.scrollOffset();
            const desktop = window.matchMedia('(min-width: 1024px)').matches;

            if (this.sidebar) {
                this.sidebar.style.top = desktop ? `${offset}px` : '';

                if (this.progress) {
                    this.sidebar.style.maxHeight = desktop ? `calc(100dvh - ${offset + 24}px)` : '';
                }
            }

            if (this.progress) {
                const rectangle = this.body.getBoundingClientRect();
                const distance = rectangle.height - (window.innerHeight - offset);
                const progress = distance > 0
                    ? Math.max(0, Math.min(1, (offset - rectangle.top) / distance))
                    : Number(rectangle.top <= offset);

                this.progress.style.top = `${offset - 24}px`;
                this.progress.hidden = rectangle.bottom <= offset || rectangle.top > window.innerHeight;
                this.progress.firstElementChild.style.transform = `scaleX(${progress})`;
            }
        };

        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
    }

    slugify(text) {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    }
}

(() => {
    if (MODULE.length) {
        MODULE.forEach(component => new PostArticle(component));
    }
})();

export default PostArticle;
